import { useEffect, useMemo } from 'react';
import { useTaskStates } from './useTaskStates';
import { getPrimaryTag } from '../../../../hooks/useTagActions';
import { isMilestoneReached } from '../../../utils/milestoneUtils';
import { paintMinimap } from '../paintMinimap';

/**
 * Paint minimap tiles and wires into one canvas whenever the graph changes.
 * Pan does not repaint this bitmap; the viewport indicator moves on its own.
 */
export function useMinimapCanvas({
  canvasRef,
  tasks,
  nodePositions,
  nodeDimensions,
  edges,
  scale,
  bounds,
  minimapWidth,
  minimapHeight,
  allCategories,
  allTags,
  lockStates,
  statuses,
  nestedSprints,
}) {
  const tagColorByName = useMemo(() => {
    const map = {};
    const source = allTags?.length ? allTags : allCategories;
    (source || []).forEach((t) => {
      if (t?.name) map[t.name] = t.color;
    });
    return map;
  }, [allCategories, allTags]);

  const taskStates = useTaskStates(tasks, statuses, lockStates, nestedSprints);

  const milestoneReachedById = useMemo(() => {
    const map = new Map();
    tasks.forEach((task) => {
      if (task.nodeType === 'milestone') {
        map.set(task.id, isMilestoneReached(task, tasks, nestedSprints, statuses));
      }
    });
    return map;
  }, [tasks, nestedSprints, statuses]);

  const primaryTagById = useMemo(() => {
    const map = {};
    tasks.forEach((task) => {
      map[task.id] = getPrimaryTag(task);
    });
    return map;
  }, [tasks]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !minimapWidth || !minimapHeight) return undefined;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.round(minimapWidth * dpr));
    const h = Math.max(1, Math.round(minimapHeight * dpr));
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    paintMinimap(ctx, {
      width: minimapWidth,
      height: minimapHeight,
      tasks,
      nodePositions,
      nodeDimensions,
      edges,
      scale,
      bounds,
      taskStates,
      tagColorByName,
      milestoneReachedById,
      primaryTagById,
    });
    return undefined;
  }, [
    canvasRef,
    tasks,
    nodePositions,
    nodeDimensions,
    edges,
    scale,
    bounds,
    minimapWidth,
    minimapHeight,
    taskStates,
    tagColorByName,
    milestoneReachedById,
    primaryTagById,
  ]);
}
